<template>
  <div class="member-page">
    <div class="form-wrapper">
      <ul class="nav nav-tabs justify-content-center mb-4">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'login' }"
            href="#"
            @click.prevent="activeTab = 'login'"
          >{{ $t('member.Login') }}</a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'register' }"
            href="#"
            @click.prevent="activeTab = 'register'"
          >{{ $t('member.Register') }}</a>
        </li>
      </ul>

      <!-- Login -->
      <div v-if="activeTab === 'login'">
        <h2 class="text-center mb-4">{{ $t('member.Login') }}</h2>

        <div v-if="loginState.error" class="alert alert-danger">
          {{ loginState.error }}
          <div v-if="loginState.unverified" class="mt-2">
            <button
              class="btn btn-outline-primary btn-sm"
              :disabled="loginState.resending || emailResend.secondsLeft > 0"
              @click="onResendVerification"
            >
              <span v-if="loginState.resending" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('member.resend') }}
              <span v-if="emailResend.secondsLeft > 0">({{ emailResend.secondsLeft }}s)</span>
            </button>
          </div>
        </div>

        <!-- reset 訊息 -->
        <div v-if="resetState.success" class="alert alert-success">{{ resetState.success }}</div>
        <div v-if="resetState.error" class="alert alert-danger">{{ resetState.error }}</div>

        <form class="mt-4" style="max-width: 400px; margin: 0 auto;" @submit.prevent="onLogin">
          <div class="mb-3">
            <label class="form-label">{{ $t('member.EmailPhone') }}</label>
            <input
              v-model.trim="loginForm.identifier"
              type="text"
              class="form-control"
              required
              autocomplete="username"
              :placeholder="$t('member.inputeEailPhone')"
            />
          </div>
          <div class="mb-3">
            <label class="form-label">{{ $t('member.Password') }}</label>
            <input v-model="loginForm.password" type="password" class="form-control" required autocomplete="current-password" />
          </div>
          <button type="submit" class="btn btn-primary w-100" :disabled="loginState.loading">
            <span v-if="loginState.loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loginState.loading ? $t('common.processing') : $t('member.Login') }}
          </button>

          <div class="text-center mt-3">
            <button type="button" class="btn btn-link p-0" :disabled="resetState.sending" @click="onSendReset">
              <span v-if="resetState.sending" class="spinner-border spinner-border-sm me-1"></span>
              {{$t('member.forget')}}
            </button>
          </div>
        </form>
      </div>

      <!-- Register -->
      <div v-else>
        <h2 class="text-center mb-4">{{ $t('member.Register') }}</h2>

        <div v-if="registerState.success" class="alert alert-success">{{ registerState.success }}</div>
        <div v-if="registerState.error" class="alert alert-danger">{{ registerState.error }}</div>

        <div class="btn-group d-flex" role="group" aria-label="register-type">
          <button
            type="button"
            class="btn"
            :class="registerMode === 'email' ? 'btn-primary' : 'btn-outline-primary'"
            @click="registerMode = 'email'"
          >
            Email 註冊
          </button>

          <button
            type="button"
            class="btn"
            :class="registerMode === 'phone' ? 'btn-primary' : 'btn-outline-primary'"
            @click="registerMode = 'phone'"
          >
            手機 OTP 註冊
          </button>
        </div>

        <!-- Email 註冊（保留） -->
        <form
          v-if="registerMode === 'email'"
          class="mt-4"
          style="max-width: 400px; margin: 0 auto;"
          @submit.prevent="onRegisterEmail"
        >
          <div class="mb-3">
            <label class="form-label">{{$t("member.Email")}}</label>
            <input v-model.trim="form.email" type="email" class="form-control" required />
          </div>
          <div class="mb-3">
            <label class="form-label">{{$t("member.Password")}}</label>
            <input v-model="form.password" type="password" class="form-control" required minlength="8" />
          </div>
          <div class="mb-3">
            <label class="form-label">{{$t("member.configPassword")}}</label>
            <input v-model="form.confirm" type="password" class="form-control" required />
          </div>
          <button
            type="submit"
            class="btn btn-primary w-100"
            :disabled="registerState.loading || emailSignup.secondsLeft > 0"
          >
            <span v-if="registerState.loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ registerState.loading ? $t('common.processing') : $t('member.sendEmail') }}
            <span v-if="emailSignup.secondsLeft > 0">({{ emailSignup.secondsLeft }}s)</span>
          </button>
        </form>

        <!-- Phone OTP 註冊 -->
        <div v-if="registerMode === 'phone'" class="mt-4" style="max-width: 420px; margin: 0 auto;">
          <div class="mb-3">
            <label class="form-label">{{ $t('member.Phone') || '手機' }}</label>
            <input
              v-model.trim="phoneForm.phone"
              type="tel"
              class="form-control"
              required
              placeholder=""
              autocomplete="tel"
            />
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('member.Password') }}</label>
            <input
              v-model="phoneForm.password"
              type="password"
              class="form-control"
              required
              minlength="8"
              autocomplete="new-password"
            />
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('member.configPassword') }}</label>
            <input
              v-model="phoneForm.confirm"
              type="password"
              class="form-control"
              required
              minlength="8"
              autocomplete="new-password"
            />
          </div>

          <button
            type="button"
            class="btn btn-primary w-100"
            :disabled="phoneState.sending || phoneOtp.secondsLeft > 0"
            @click="onRegisterPhoneSendOtp"
          >
            <span v-if="phoneState.sending" class="spinner-border spinner-border-sm me-2"></span>
            {{ phoneState.sending ? $t('common.processing') : ($t('member.sendOtp') || '發送驗證碼') }}
            <span v-if="phoneOtp.secondsLeft > 0">({{ phoneOtp.secondsLeft }}s)</span>
          </button>
          <div v-if="phoneState.success" class="alert alert-success mt-3">{{ phoneState.success }}</div>
        </div>
      </div>
    </div>

    <!-- ✅ decor 不需要了（背景直接用 mainpage.jpg） -->
    <div class="decor" aria-hidden="true"></div>

    <!-- ✅ Success 提示 Modal（共用：用 loginState.success 當文字） -->
    <div v-if="successModal.show" class="success-modal" @click.self="closeSuccessModal">
      <div class="success-card">
        <h3 class="mb-2">{{ $t('common.success') || '成功' }}</h3>
        <div class="success-text">{{ loginState.success }}</div>

        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-primary" @click="closeSuccessModal">
            {{ $t('common.ok') || '確定' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reset 密碼 Modal（保留） -->
    <div v-if="resetState.showModal" class="reset-modal" @click.self.stop="noop">
      <div class="reset-card">
        <h3 class="mb-3">{{ $t('member.setnewPassword') }}</h3>

        <div v-if="resetState.modalError" class="alert alert-danger">{{ resetState.modalError }}</div>

        <div class="mb-3">
          <label class="form-label" for="npw">{{ $t('member.newPassword') }}</label>
          <input
            id="npw"
            v-model="resetForm.password"
            type="password"
            class="form-control"
            minlength="8"
            :placeholder="$t('passwordUpdate.minLength')"
          />
        </div>

        <div class="mb-3">
          <label class="form-label" for="cpw">{{ $t('member.configNewPassword') }}</label>
          <input
            id="cpw"
            v-model="resetForm.confirm"
            type="password"
            class="form-control"
            minlength="8"
          />
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-primary" :disabled="resetState.updating" @click="onConfirmNewPassword">
            <span v-if="resetState.updating" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('member.configChange') }}
          </button>
          <button class="btn btn-outline-secondary" @click="closeResetModal">
            {{ $t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- OTP 驗證 Modal -->
    <div
      v-if="otpModal.show"
      class="otp-modal"
      @click.self.stop="onOtpBackdropClick"
    >
      <div class="otp-card" @click.stop>
        <h3 class="mb-3">
          {{
            otpModal.purpose === 'reset_login'
              ? ($t('member.otpLogin') || '簡訊驗證登入')
              : ($t('member.otpVerify') || '輸入簡訊驗證碼')
          }}
        </h3>

        <div v-if="otpModal.error" class="alert alert-danger">{{ otpModal.error }}</div>

        <div class="mb-3">
          <label class="form-label">{{ $t('member.otpCode') || '驗證碼' }}</label>
          <input
            v-model.trim="otpModal.code"
            type="text"
            inputmode="numeric"
            class="form-control"
            placeholder="xxxxxx"
            maxlength="6"
          />
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-primary" :disabled="otpModal.verifying" @click="onVerifyPhoneOtp">
            <span v-if="otpModal.verifying" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('member.verify') || '驗證' }}
          </button>

          <button
            class="btn btn-outline-secondary"
            :disabled="otpModal.resending || phoneOtp.secondsLeft > 0"
            @click="onResendPhoneOtp"
          >
            <span v-if="otpModal.resending" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('member.resend') }}
            <span v-if="phoneOtp.secondsLeft > 0">({{ phoneOtp.secondsLeft }}s)</span>
          </button>

          <button class="btn btn-outline-danger ms-auto" @click="closeOtpModalSafe">
            {{ $t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ⭐ 會員條款連結 ⭐ -->
    <div class="legal-foot" role="note" :aria-label="$t('member.privacyLinkLabel')">
      {{ $t('member.loginConfigAgree') }}
      <a
        class="legal-link"
        :href="termsHref"
        target="_blank"
        rel="noopener"
      >
        《{{ $t('member.Privacy') }}》
      </a>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'

export default {
  name: 'Member',
  data () {
    return {
      activeTab: 'login',

      // login
      loginForm: { identifier: '', password: '' },
      loginState: { loading: false, error: '', success: '', unverified: false, resending: false },

      // register
      registerMode: 'email',
      form: { email: '', password: '', confirm: '' },
      registerState: { loading: false, error: '', success: '' },

      // phone register (OTP)
      phoneForm: { phone: '', password: '', confirm: '' },
      phoneState: { sending: false, error: '', success: '', lastPhoneE164: '' },

      otpModal: {
        show: false,
        code: '',
        verifying: false,
        resending: false,
        error: '',
        purpose: 'register',
        phoneE164: ''
      },

      successModal: { show: false },

      // reset
      resetForm: { password: '', confirm: '' },
      resetState: { sending: false, success: '', error: '', showModal: false, updating: false, modalError: '' },

      authSub: null,

      emailResend: { key: '', secondsLeft: 0, timer: null },
      emailSignup: { key: '', secondsLeft: 0, timer: null },
      phoneOtp: { key: '', secondsLeft: 0, timer: null }
    }
  },

  async mounted () {
    this.authSub = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem('recovery_hold', '1')
        this.activeTab = 'login'
        this.resetState.showModal = true
      }
    }).data.subscription

    const handled = await this.handleEmailRedirectResult()
    if (handled) return

    if (sessionStorage.getItem('recovery_hold') === '1') {
      this.activeTab = 'login'
      this.resetState.showModal = true
      return
    }

    const q2 = new URLSearchParams(window.location.search)
    const h2 = new URLSearchParams((window.location.hash || '').replace(/^#/, ''))
    const urlHasAuthParams =
      q2.has('code') || q2.has('token') || q2.has('token_hash') ||
      h2.has('code') || h2.has('token') || h2.has('token_hash') ||
      h2.has('access_token') || h2.has('refresh_token') || h2.has('type')

    if (!urlHasAuthParams) {
      const { data: sess } = await supabase.auth.getSession()
      if (sess?.session) {
        this.$router.replace('/app')
        return
      }
    }

    this._cdLoad('emailResend', this.loginForm.identifier)
    this._cdLoad('emailSignup', this.form.email)
    this._cdLoad('phoneOtp', this.phoneState.lastPhoneE164 || this.phoneForm.phone)
  },

  beforeUnmount () {
    this.authSub?.unsubscribe?.()
    this._cdStopAll()
  },

  methods: {
    onOtpBackdropClick () {
      // 點遮罩不要關，改成提示
      this.otpModal.error = this.$t('member.otpDontCloseTip') || '請輸入驗證碼，若要關閉請按「取消」'
    },

    closeOtpModalSafe () {
      // 如果已輸入一些碼，避免手滑
      const hasInput = (this.otpModal.code || '').trim().length > 0
      if (hasInput) {
        const ok = window.confirm(this.$t('member.otpCloseConfirm') || '已輸入驗證碼，確定要關閉嗎？')
        if (!ok) return
      }
      this.closeOtpModal()
    },

    _cdStorageKey (name, identity) {
      const v = (identity || '').trim().toLowerCase()
      return `mg_cd_${name}_${v}`
    },
    _cdNow () { return Date.now() },
    _cdGetRemainingSeconds (storageKey, cooldownMs = 60000) {
      const last = parseInt(localStorage.getItem(storageKey) || '0', 10)
      if (!last) return 0
      const remain = cooldownMs - (this._cdNow() - last)
      return remain > 0 ? Math.ceil(remain / 1000) : 0
    },
    _cdStart (name, identity, cooldownMs = 60000) {
      const storageKey = this._cdStorageKey(name, identity)
      localStorage.setItem(storageKey, String(this._cdNow()))

      const slot = this[name]
      if (!slot) return
      slot.key = storageKey

      if (slot.timer) clearInterval(slot.timer)
      slot.secondsLeft = this._cdGetRemainingSeconds(storageKey, cooldownMs)

      slot.timer = setInterval(() => {
        slot.secondsLeft = this._cdGetRemainingSeconds(storageKey, cooldownMs)
        if (slot.secondsLeft <= 0) {
          clearInterval(slot.timer)
          slot.timer = null
        }
      }, 250)
    },
    _cdLoad (name, identity, cooldownMs = 60000) {
      const storageKey = this._cdStorageKey(name, identity)
      const remain = this._cdGetRemainingSeconds(storageKey, cooldownMs)

      const slot = this[name]
      if (!slot) return
      slot.key = storageKey

      if (slot.timer) clearInterval(slot.timer)
      slot.secondsLeft = remain

      if (remain > 0) {
        slot.timer = setInterval(() => {
          slot.secondsLeft = this._cdGetRemainingSeconds(storageKey, cooldownMs)
          if (slot.secondsLeft <= 0) {
            clearInterval(slot.timer)
            slot.timer = null
          }
        }, 250)
      }
    },
    _cdStopAll () {
      ['emailResend', 'emailSignup', 'phoneOtp'].forEach(k => {
        const s = this[k]
        if (s?.timer) clearInterval(s.timer)
        if (s) s.timer = null
      })
    },

    noop () {},
    isEmail (v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) },
    toE164Phone (input) {
      const v = (input || '').replace(/\s|-/g, '')
      if (!v) return ''
      if (v.startsWith('+')) return v
      if (/^09\d{8}$/.test(v)) return '+886' + v.slice(1)
      return v
    },

    translateSupabaseError (message) {
      if (!message) return this.$t('loginState.errorTryLater')
      const msg = String(message).toLowerCase()
      if (msg.includes('invalid login credentials')) return this.$t('loginState.error')
      if (msg.includes('email not confirmed') || msg.includes('confirm')) return this.$t('loginState.unverified')
      if (msg.includes('invalid phone') || msg.includes('phone')) return this.$t('loginState.invalidPhone')
      if (msg.includes('expired') || msg.includes('token')) {
        return this.$t('resetState.invalidOrExpiredLink') || this.$t('loginState.errorTryLater')
      }
      return String(message)
    },

    async onLogin () {
      this.loginState.error = ''
      this.loginState.success = ''
      this.loginState.unverified = false

      const { identifier, password } = this.loginForm
      if (!identifier || !password) {
        this.loginState.error = this.$t('loginState.inputRequired')
        return
      }

      this.loginState.loading = true
      try {
        let res, error
        if (this.isEmail(identifier)) {
          ({ data: res, error } = await supabase.auth.signInWithPassword({ email: identifier, password }))
        } else {
          const phone = this.toE164Phone(identifier)
          if (!phone.startsWith('+')) throw new Error(this.$t('loginState.invalidPhone'))
          ;({ data: res, error } = await supabase.auth.signInWithPassword({ phone, password }))
        }

        if (error) {
          const fp = `${error.message || ''} ${error.status || ''} ${error.name || ''}`.toLowerCase()
          if (fp.includes('confirm') || fp.includes('not confirmed') || fp.includes('email_not_confirmed')) {
            this.loginState.error = this.$t('loginState.unverified')
            this.loginState.unverified = true
            return
          }
          throw error
        }

        const user = res?.user
        if (user && user.email && !user.email_confirmed_at) {
          this.loginState.error = this.$t('loginState.unverified')
          this.loginState.unverified = true
          await supabase.auth.signOut()
          return
        }

        this.loginState.success = this.$t('loginState.success')

        // 每日唯一登入紀錄
        try {
          const userId = res.user.id
          const todayStart = new Date()
          todayStart.setHours(0, 0, 0, 0)

          const { data: existing } = await supabase
            .from('login_logs')
            .select('id')
            .eq('user_id', userId)
            .gte('logged_at', todayStart.toISOString())
            .limit(1)

          if (!existing || existing.length === 0) {
            await supabase.from('login_logs').insert({
              user_id: userId,
              device: navigator.userAgent || null
            })
          }
        } catch (e) {
          console.warn('login log failed:', e)
        }

        this.$router.replace('/app')
      } catch (err) {
        this.loginState.error = this.translateSupabaseError(err?.message || err)
      } finally {
        this.loginState.loading = false
      }
    },

    async onResendVerification () {
      this.loginState.error = ''
      this.loginState.success = ''
      this.loginState.resending = true
      try {
        const email = this.isEmail(this.loginForm.identifier) ? this.loginForm.identifier.trim() : ''
        if (!email) {
          this.loginState.error = this.$t('loginState.resendInputEmailFirst')
          return
        }

        const redirectToBase = `${window.location.origin}${this.$route?.fullPath || '/member'}`
        const redirectTo = this._buildRedirectTo(redirectToBase, { flow: 'signup' })
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: { emailRedirectTo: redirectTo }
        })
        if (error) throw error

        this.loginState.success = this.$t('loginState.resent')
        this._cdStart('emailResend', email)
      } catch (err) {
        this.loginState.error = (typeof err?.message === 'string' && err.message) || String(err || this.$t('loginState.resendError'))
      } finally {
        this.loginState.resending = false
      }
    },

    async onRegisterEmail () {
      this.registerState.error = ''
      this.registerState.success = ''

      const email = (this.form.email || '').trim()
      const password = (this.form.password || '').trim()
      const confirm = (this.form.confirm || '').trim()

      if (password !== confirm) {
        this.registerState.error = this.$t('registerState.mismatch')
        return
      }
      if (!email || !password) {
        this.registerState.error = this.$t('registerState.formIncomplete')
        return
      }
      if (this.emailSignup?.secondsLeft > 0) return

      this.registerState.loading = true
      try {
        const { data: available, error: chkErr } = await supabase
          .rpc('check_email_available', { p_email: email.toLowerCase() })
        if (chkErr) throw chkErr
        if (!available) {
          this.registerState.error = this.$t('registerState.emailAlreadyUsed')
          return
        }

        const redirectToBase = `${window.location.origin}${this.$route?.fullPath || '/member'}`
        const redirectTo = this._buildRedirectTo(redirectToBase, { flow: 'signup' })
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: redirectTo }
        })
        if (error) throw error

        this.registerState.success = ''
        this.openSuccessModal(this.$t('registerState.success'))

        this._cdStart('emailSignup', email)
        this.form = { email: '', password: '', confirm: '' }
      } catch (err) {
        this.registerState.error = (typeof err?.message === 'string' && err.message) || this.$t('registerState.error')
      } finally {
        this.registerState.loading = false
      }
    },

    async onRegisterPhoneSendOtp () {
      this.phoneState.error = ''
      this.phoneState.success = ''
      this.registerState.error = ''
      this.registerState.success = ''

      const phoneRaw = (this.phoneForm.phone || '').trim()
      const password = (this.phoneForm.password || '').trim()
      const confirm = (this.phoneForm.confirm || '').trim()

      if (!phoneRaw || !password) {
        this.phoneState.error = this.$t('registerState.formIncomplete')
        return
      }
      if (password.length < 8) {
        this.phoneState.error = this.$t('passwordUpdate.minLength')
        return
      }
      if (password !== confirm) {
        this.phoneState.error = this.$t('registerState.mismatch')
        return
      }

      const phone = this.toE164Phone(phoneRaw)
      if (!phone.startsWith('+')) {
        this.phoneState.error = this.$t('loginState.invalidPhone')
        return
      }
      if (this.phoneOtp?.secondsLeft > 0) return

      this.phoneState.sending = true
      try {
        // 1) 先確認手機是否可用（你原本就有）
        const { data: available, error: chkErr } = await supabase
          .rpc('check_phone_available', { p_phone: phone })
        if (chkErr) throw chkErr
        if (!available) {
          this.registerState.error = this.$t('registerState.phoneAlreadyUsed')
          return
        }

        // ✅ 2) 用 signUp 建立帳號（phone + password），Supabase 會寄/發 OTP 做驗證
        const { error } = await supabase.auth.signUp({
          phone,
          password
        })
        if (error) throw error

        // 3) UI 狀態
        this.phoneState.lastPhoneE164 = phone
        this._cdStart('phoneOtp', phone)
        this.phoneState.success = this.$t('registerState.otpSent')

        // ✅ 4) 開 OTP modal，順手把目的與 phoneE164 塞好（避免殘留狀態害你走錯分支）
        this.otpModal.show = true
        this.otpModal.code = ''
        this.otpModal.error = ''
        this.otpModal.purpose = 'register'
        this.otpModal.phoneE164 = phone
      } catch (e) {
        this.phoneState.error = this.translateSupabaseError(e?.message) || this.$t('registerState.error')
      } finally {
        this.phoneState.sending = false
      }
    },

    async onVerifyPhoneOtp () {
      this.otpModal.error = ''
      this.otpModal.verifying = true
      try {
        const phone =
          this.otpModal.phoneE164 ||
          this.phoneState.lastPhoneE164 ||
          this.toE164Phone(this.phoneForm.phone)

        const token = (this.otpModal.code || '').trim()

        if (!phone || !phone.startsWith('+')) throw new Error(this.$t('loginState.invalidPhone'))
        if (!token) throw new Error(this.$t('member.otpRequired') || '請輸入驗證碼')

        // ✅ 驗證 OTP
        const { error } = await supabase.auth.verifyOtp({ type: 'sms', phone, token })
        if (error) throw error

        // ✅ 忘記密碼 / OTP 登入：照你原本的
        if (this.otpModal.purpose === 'reset_login') {
          this.closeOtpModal()
          this.loginState.success = this.$t('loginState.success') || '登入成功'
          this.$router.replace('/app')
          return
        }

        // ✅ 註冊：A 方案已經在 signUp 時把 password 設定好了，這裡不要 updateUser
        this.closeOtpModal()
        this.phoneForm = { phone: '', password: '', confirm: '' }

        // 想保留你原本「切回 login tab」也行，但你說要直接跳登入，所以直接進 app
        this.activeTab = 'login'
        this.loginForm.identifier = phone
        this.loginState.success = this.$t('loginState.success') || '登入成功'
        this.$router.replace('/app')
      } catch (e) {
        this.otpModal.error = this.translateSupabaseError(e?.message)
      } finally {
        this.otpModal.verifying = false
      }
    },

    async onResendPhoneOtp () {
      this.otpModal.error = ''
      this.otpModal.resending = true
      try {
        const phone = this.otpModal.phoneE164 || this.phoneState.lastPhoneE164 || this.toE164Phone(this.phoneForm.phone)
        if (!phone || !phone.startsWith('+')) throw new Error(this.$t('loginState.invalidPhone'))

        const { error } = await supabase.auth.signInWithOtp({ phone })
        if (error) throw error

        this.phoneState.success = this.$t('loginState.resent')
        this._cdStart('phoneOtp', phone)
      } catch (e) {
        this.otpModal.error = this.translateSupabaseError(e?.message)
      } finally {
        this.otpModal.resending = false
      }
    },

    closeOtpModal () {
      this.otpModal.show = false
      this.otpModal.code = ''
      this.otpModal.error = ''
    },

    async onSendReset () {
      this.resetState.error = ''
      this.resetState.success = ''
      this.resetState.sending = true

      try {
        const idt = (this.loginForm.identifier || '').trim()
        if (!idt) throw new Error(this.$t('loginState.inputRequired') || '請輸入 Email 或手機')

        if (this.isEmail(idt)) {
          const redirectTo = `${window.location.origin}/member?flow=recovery`
          const { error } = await supabase.auth.resetPasswordForEmail(idt, { redirectTo })
          if (error) throw error
          this.resetState.success = this.$t('resetState.success')
          return
        }

        const phone = this.toE164Phone(idt)
        if (!phone.startsWith('+')) throw new Error(this.$t('loginState.invalidPhone'))

        try {
          const { data: available, error: chkErr } = await supabase
            .rpc('check_phone_available', { p_phone: phone })
          if (chkErr) throw chkErr
          if (available) throw new Error(this.$t('resetState.phoneNotRegistered') || '此手機尚未註冊')
        } catch (e) {
          if (e?.message) throw e
        }

        if (this.phoneOtp?.secondsLeft > 0) return

        const { error } = await supabase.auth.signInWithOtp({ phone })
        if (error) throw error

        this.resetState.success = this.$t('resetState.otpSent') || '已發送簡訊驗證碼'

        this.otpModal.show = true
        this.otpModal.code = ''
        this.otpModal.error = ''
        this.otpModal.purpose = 'reset_login'
        this.otpModal.phoneE164 = phone

        this._cdStart('phoneOtp', phone)
      } catch (err) {
        this.resetState.error = (typeof err?.message === 'string' && err.message) || this.$t('resetState.error')
      } finally {
        this.resetState.sending = false
      }
    },

    async onConfirmNewPassword () {
      this.resetState.modalError = ''
      this.resetState.updating = true
      try {
        const p1 = (this.resetForm.password || '').trim()
        const p2 = (this.resetForm.confirm || '').trim()
        if (!p1 || p1.length < 8) throw new Error(this.$t('passwordUpdate.minLength'))
        if (p1 !== p2) throw new Error(this.$t('passwordUpdate.mismatch'))

        const { error } = await supabase.auth.updateUser({ password: p1 })
        if (error) throw error

        sessionStorage.removeItem('recovery_hold')
        if (typeof this.clearUrl === 'function') this.clearUrl()

        this.resetState.showModal = false
        this.resetForm.password = ''
        this.resetForm.confirm = ''
        this.loginState.success = this.$t('passwordUpdate.successLogin')
        this.$router.replace('/app')
      } catch (e) {
        this.resetState.modalError = (typeof e?.message === 'string' && e.message) || this.$t('passwordUpdate.error')
      } finally {
        this.resetState.updating = false
      }
    },

    closeResetModal () {
      sessionStorage.removeItem('recovery_hold')
      this.resetState.showModal = false
      this.resetState.modalError = ''
    },

    async handleEmailRedirectResult () {
      try {
        const q = new URLSearchParams(window.location.search)
        const h = new URLSearchParams((window.location.hash || '').replace(/^#/, ''))

        const errCode = q.get('error_code') || h.get('error_code')
        const errDesc = q.get('error_description') || h.get('error_description')
        const typeTag = (h.get('type') || q.get('type') || '').toLowerCase()
        const flowTag = (q.get('flow') || h.get('flow') || '').toLowerCase()

        const isRecoveryTag = typeTag === 'recovery' || flowTag === 'recovery'
        const tokenHash = isRecoveryTag ? this._getRecoveryTokenFromUrl(q, h) : ''

        const invalidLinkMsg = this.$t('resetState.invalidOrExpiredLink')

        if (errCode) {
          const { data: sessA } = await supabase.auth.getSession()
          if (sessA?.session && (isRecoveryTag || tokenHash)) {
            sessionStorage.setItem('recovery_hold', '1')
            this.activeTab = 'login'
            this.resetState.showModal = true
            return true
          }
          this.activeTab = 'login'
          this.resetState.error = decodeURIComponent(errDesc || '') || invalidLinkMsg
          return true
        }

        if (tokenHash) {
          try {
            const { error: vErr } = await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash })
            if (vErr) throw vErr
            sessionStorage.setItem('recovery_hold', '1')
            this.activeTab = 'login'
            this.resetState.showModal = true
          } catch (e) {
            const { data: sessB } = await supabase.auth.getSession()
            if (sessB?.session) {
              sessionStorage.setItem('recovery_hold', '1')
              this.activeTab = 'login'
              this.resetState.showModal = true
            } else {
              this.resetState.error = (typeof e?.message === 'string' && e.message) || invalidLinkMsg
            }
          }
          return true
        }

        if (isRecoveryTag) {
          sessionStorage.setItem('recovery_hold', '1')
          this.activeTab = 'login'
          this.resetState.showModal = true
          return true
        }

        const { data: userRes } = await supabase.auth.getUser()
        if (!isRecoveryTag && userRes?.user?.email_confirmed_at) {
          this.$router.replace('/app')
          return true
        }

        const errHash = h.get('error_description')
        if (errHash) {
          this.activeTab = 'login'
          this.loginState.error = decodeURIComponent(errHash) || this.$t('loginState.errorTryLater')
          return true
        }

        return false
      } catch {
        return false
      }
    },

    _buildRedirectTo (baseUrl, extra = {}) {
      const u = new URL(baseUrl, window.location.origin)
      Object.entries(extra).forEach(([k, v]) => u.searchParams.set(k, String(v)))
      return u.toString()
    },
    _getRecoveryTokenFromUrl (q, h) {
      return (
        q.get('token_hash') || q.get('code') || q.get('token') ||
        h.get('token_hash') || h.get('code') || h.get('token') || ''
      )
    },

    openSuccessModal (msg) {
      this.loginState.success = msg || ''
      this.successModal.show = !!this.loginState.success
    },

    closeSuccessModal () {
      this.successModal.show = false
      this.activeTab = 'login'
    },

    clearUrl () { history.replaceState(null, document.title, window.location.pathname) }
  }
}
</script>

<style scoped>
/* ✅ 桌機：整頁改成「左 1/3 放登入窗、右 2/3 空出來看背景」 */
.member-page {
  position: relative;
  min-height: 100vh;
  background: url('~@/assets/images/omnimainpage.jpg') no-repeat center / cover;

  /* 手機預設：置中 */
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 2rem;
}

/* ✅ 登入窗本體（保留你深紫玻璃感） */
.form-wrapper {
  width: 100%;
  max-width: 420px;
  padding: 2rem;
  border-radius: 14px;

  background: rgba(28, 12, 48, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 14px 40px rgba(0,0,0,.28);
  backdrop-filter: blur(10px);

  color: rgba(255,255,255,.92);
}

/* ✅ 桌機版（>= 900px）：改成 grid，登入窗在左 1/3 並垂直置中 */
@media (min-width: 900px) {
  .member-page {
    display: grid;
    grid-template-columns: 1fr 2fr; /* 左 1/3、右 2/3 */
    align-items: center;           /* 垂直置中 */
    justify-items: start;          /* 靠左 */
    padding: 0;                    /* 背景圖自己滿版 */
  }

  .form-wrapper {
    margin-left: clamp(20px, 4vw, 64px); /* 距離左邊一點，不貼邊 */
  }
}

/* 標題字 */
.form-wrapper h2 {
  color: #fff;
  font-weight: 700;
}

/* Tabs：讓 active 是深紫亮一點，文字白 */
.form-wrapper .nav-tabs {
  border-bottom: 1px solid rgba(255,255,255,.16);
}
.form-wrapper .nav-tabs .nav-link {
  border: 0;
  color: rgba(255,255,255,.75);
  border-radius: 10px;
}
.form-wrapper .nav-tabs .nav-link:hover {
  color: #fff;
  background: rgba(255,255,255,.08);
}
.form-wrapper .nav-tabs .nav-link.active {
  color: #fff;
  background: rgba(255,255,255,.12);
}

/* label */
.form-wrapper .form-label {
  color: rgba(255,255,255,.88);
  font-weight: 600;
}

/* input：深紫底、白字、柔邊框 */
.form-wrapper .form-control {
  background: rgba(12, 6, 22, 0.55);
  color: #fff;
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 12px;
}
.form-wrapper .form-control:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(167, 139, 250, .25); /* 紫色焦點 */
  border-color: rgba(167, 139, 250, .65);
}
.form-wrapper .form-control::placeholder {
  color: rgba(255,255,255,.55);
  opacity: 1;
}

/* 主按鈕：深紫 → 亮紫 */
.form-wrapper .btn-primary {
  border: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b1c6a, #6d28d9);
}
.form-wrapper .btn-primary:hover {
  filter: brightness(1.06);
}
.form-wrapper .btn-outline-primary {
  border-radius: 12px;
  border-color: rgba(167, 139, 250, .65);
  color: rgba(255,255,255,.92);
}
.form-wrapper .btn-outline-primary:hover {
  background: rgba(167, 139, 250, .18);
  border-color: rgba(167, 139, 250, .85);
}

/* ✅ 右邊按鈕：取消左側圓角（貼合左邊按鈕） */
.btn-group[aria-label="register-type"] .btn:last-child {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

/* （可選）左邊按鈕：取消右側圓角，兩顆才會完全無縫 */
.btn-group[aria-label="register-type"] .btn:first-child {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

/* （可選）避免中間有縫或重疊感 */
.btn-group[aria-label="register-type"] .btn + .btn {
  margin-left: 0 !important;
}


/* link（忘記密碼） */
.form-wrapper .btn-link {
  color: rgba(255,255,255,.82);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.form-wrapper .btn-link:hover {
  color: #fff;
}

/* decor 不用顯示 */
.decor { display: none; }

/* Modals：保持原本樣式即可（想要一起紫也能再染） */
.reset-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1000;
}
.reset-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,.18);
  padding: 18px;
}

.otp-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1100;
}
.otp-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,.18);
  padding: 18px;
}

.success-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1200;
}
.success-card {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 34px rgba(0,0,0,.22);
  padding: 18px;
}
.success-text {
  font-size: 1.02rem;
  line-height: 1.6;
  word-break: break-word;
}

/* 底部提示條：改成深紫透明，搭配整體 */
.legal-foot {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: max(12px, env(safe-area-inset-bottom, 0px));
  font-size: 0.92rem;
  color: rgba(255,255,255,.92);

  background: rgba(28, 12, 48, 0.78);
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.12);
  box-shadow: 0 10px 28px rgba(0,0,0,.22);
  backdrop-filter: blur(8px);

  line-height: 1.6;
  text-align: center;
  z-index: 2000;
  white-space: nowrap;
}
.legal-link {
  color: #c4b5fd; /* 淡紫 */
  text-decoration: underline;
  text-underline-offset: 2px;
}
.legal-link:active { opacity: .85; }

@media (max-width: 420px) {
  .legal-foot {
    font-size: 0.88rem;
    padding: 6px 10px;
    white-space: normal;
    max-width: calc(100vw - 24px);
  }
}
</style>
